import React from 'react';
import { FileText, Shield, User, CreditCard, AlertTriangle, Scale } from 'lucide-react';
import Header from '../../components/common/Header';
import Footer from '../../components/common/Footer';

const TermsAndConditions = () => {
  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute transform -rotate-12 right-0 top-40 w-72 h-72 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                <FileText className="w-4 h-4" />
                <span className="text-sm font-medium">Legal Agreement</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                Terms and Conditions
              </h1>
              <p className="text-lg md:text-xl text-indigo-100 max-w-2xl mx-auto">
                Please read these terms carefully before using our platform
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 space-y-8">
            <p className="text-gray-600 text-sm">
              <strong>Effective Date:</strong> January 1, 2025
            </p>

            <p className="text-gray-700 leading-relaxed">
              Welcome to <strong>HomeCure</strong>, a brand of <strong>Sanghmitra Healthcare Private Limited</strong>! These Terms and Conditions ("Terms") govern your use of our platform, website, mobile application, and services (collectively, the "Platform"). By accessing or using our Platform, you agree to be bound by these Terms. If you do not agree, please do not use our services.
            </p>

            {/* Section 1 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">1. GENERAL OVERVIEW</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>1.1 About HomeCure</strong></p>
                <p>HomeCure, operated by <strong>Sanghmitra Healthcare Private Limited</strong>, is a comprehensive healthcare service aggregator platform that connects users with healthcare providers, including doctors, diagnostic labs, pharmacies, and paramedical service providers. We facilitate bookings, consultations, and service delivery but do not directly provide medical services.</p>

                <p><strong>1.2 Acceptance of Terms</strong></p>
                <p>By creating an account, booking a service, or using any feature of the Platform, you confirm that you have read, understood, and agreed to these Terms, as well as our Privacy Policy.</p>

                <p><strong>1.3 Eligibility</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You must be at least 18 years old to use our Platform.</li>
                  <li>If you are booking services for a minor or dependent, you represent that you have the legal authority to do so.</li>
                </ul>
              </div>
            </section>

            {/* Section 2 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">2. ACCOUNT REGISTRATION AND SECURITY</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>2.1 Account Creation</strong></p>
                <p>To access certain features, you must create an account by providing accurate and complete information, including:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Full name</li>
                  <li>Mobile number (verified via OTP)</li>
                  <li>Email address</li>
                  <li>Residential address</li>
                </ul>

                <p><strong>2.2 Account Responsibility</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                  <li>You agree to notify us immediately of any unauthorized access to your account.</li>
                  <li>HomeCure is not liable for any loss or damage arising from your failure to protect your account information.</li>
                </ul>

                <p><strong>2.3 Account Termination</strong></p>
                <p>We reserve the right to suspend or terminate your account if you violate these Terms, engage in fraudulent activity, or misuse the Platform.</p>
              </div>
            </section>

            {/* Section 3 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-green-500 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">3. SERVICES OFFERED</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>HomeCure facilitates the following services:</p>

                <p><strong>3.1 Doctor Consultations</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>In-person appointments:</strong> Book consultations with doctors at their clinics.</li>
                  <li><strong>Video consultations:</strong> Connect with doctors via secure video calls.</li>
                </ul>

                <p><strong>3.2 Diagnostic and Medical Tests</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Book lab tests and health check-up packages.</li>
                  <li>Home sample collection available for select tests.</li>
                </ul>

                <p><strong>3.3 Pharmacy and Medicines</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Order prescription and over-the-counter (OTC) medicines.</li>
                  <li>Prescription verification is mandatory for prescription drugs.</li>
                </ul>

                <p><strong>3.4 Paramedical Services</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Book services such as nursing care, physiotherapy, and other home healthcare services.</li>
                </ul>

                <p><strong>3.5 Emergency Services</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Request ambulance services (where available).</li>
                </ul>
              </div>
            </section>

            {/* Section 4 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">4. BOOKING AND CANCELLATION POLICY</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>4.1 Booking Confirmation</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>All bookings are subject to availability and confirmation by the service provider.</li>
                  <li>You will receive a booking confirmation via SMS, email, or in-app notification.</li>
                </ul>

                <p><strong>4.2 Cancellation by User</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Doctor Consultations & Online Consultations:</strong> Cancellations not allowed. Rescheduling available based on doctor availability.</li>
                  <li><strong>Medical Tests & Packages:</strong> Cancellations allowed before our representative arrives. Rescheduling also available. Full refund processed within 72 working hours if canceled.</li>
                  <li><strong>Paramedical Services:</strong> Cancellations not allowed. Rescheduling available based on provider availability.</li>
                  <li><strong>Medicines:</strong> No refunds after delivery. Replacement only for wrong/defective products.</li>
                  <li>Detailed cancellation and refund policies are available in our Cancellations and Refunds Policy.</li>
                </ul>

                <p><strong>4.3 Cancellation by Service Provider</strong></p>
                <p>If a service provider cancels your booking, we will:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Notify you immediately.</li>
                  <li>Offer an alternative slot or provider.</li>
                  <li>Process a full refund if you choose not to reschedule.</li>
                </ul>

                <p><strong>4.4 No-Show Policy</strong></p>
                <p>If you fail to attend a scheduled appointment without prior cancellation, you may forfeit any refund eligibility and may be subject to a cancellation fee.</p>
              </div>
            </section>

            {/* Section 5 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">5. PAYMENTS AND PRICING</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>5.1 Pricing</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>All prices displayed on the Platform are in Indian Rupees (₹) and inclusive of applicable taxes unless otherwise stated.</li>
                  <li>Service providers may update pricing at any time. The price at the time of booking confirmation applies.</li>
                </ul>

                <p><strong>5.2 Payment Methods</strong></p>
                <p>We accept the following payment methods:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Credit/Debit Cards</li>
                  <li>UPI (Unified Payments Interface)</li>
                  <li>Net Banking</li>
                  <li>Digital Wallets</li>
                  <li>Cash on Delivery (for select services)</li>
                </ul>

                <p><strong>5.3 Payment Security</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>All online payments are processed through secure, PCI-DSS compliant payment gateways.</li>
                  <li>HomeCure does not store your complete card details.</li>
                </ul>

                <p><strong>5.4 Invoices and Receipts</strong></p>
                <p>You will receive a digital invoice/receipt for all transactions via email or within the app.</p>
              </div>
            </section>

            {/* Section 6 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">6. PRESCRIPTIONS AND MEDICAL RECORDS</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>6.1 Prescription Upload</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>For prescription medicines, you must upload a valid prescription signed by a registered medical practitioner.</li>
                  <li>HomeCure reserves the right to reject orders if prescriptions are unclear, invalid, or suspected to be fraudulent.</li>
                </ul>

                <p><strong>6.2 Medical Records</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>You may upload medical records, reports, and documents to facilitate consultations.</li>
                  <li>By uploading, you grant HomeCure the right to share these documents with the concerned healthcare provider.</li>
                </ul>

                <p><strong>6.3 Data Retention</strong></p>
                <p>Medical records and prescriptions are stored securely as per our Privacy Policy and applicable laws.</p>
              </div>
            </section>

            {/* Section 7 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">7. USER RESPONSIBILITIES</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>7.1 Accurate Information</strong></p>
                <p>You agree to provide truthful and accurate information during registration, booking, and consultations.</p>

                <p><strong>7.2 Prohibited Conduct</strong></p>
                <p>You must not:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Use the Platform for any unlawful purpose.</li>
                  <li>Harass, abuse, or threaten service providers or other users.</li>
                  <li>Attempt to hack, reverse-engineer, or disrupt the Platform.</li>
                  <li>Post false reviews or ratings.</li>
                  <li>Share or resell your account access.</li>
                </ul>

                <p><strong>7.3 Compliance with Medical Advice</strong></p>
                <p>You acknowledge that:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>HomeCure is a facilitator and not a medical advisor.</li>
                  <li>Any medical advice, diagnosis, or treatment is provided solely by the healthcare professionals you consult.</li>
                  <li>You should follow up with appropriate medical care as advised by your healthcare provider.</li>
                </ul>
              </div>
            </section>

            {/* Section 8 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">8. INTELLECTUAL PROPERTY</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>8.1 Ownership</strong></p>
                <p>All content on the Platform, including logos, trademarks, text, graphics, software, and design, is the property of HomeCure or its licensors and is protected by intellectual property laws.</p>

                <p><strong>8.2 Limited License</strong></p>
                <p>You are granted a limited, non-exclusive, non-transferable license to access and use the Platform for personal, non-commercial purposes.</p>

                <p><strong>8.3 Restrictions</strong></p>
                <p>You may not:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Copy, modify, or distribute any content from the Platform without prior written consent.</li>
                  <li>Use our trademarks or branding without authorization.</li>
                </ul>
              </div>
            </section>

            {/* Section 9 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">9. REFUNDS AND DISPUTES</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>9.1 Refund Eligibility</strong></p>
                <p>Refunds are processed in the following scenarios:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Medical Tests & Packages:</strong> Cancellation before representative arrival - 100% refund within 72 working hours.</li>
                  <li><strong>Medicines:</strong> Pre-dispatch cancellations only - refund within 5-7 business days.</li>
                  <li>Service provider cancellation.</li>
                  <li>Service not delivered as per booking.</li>
                  <li>Duplicate payment or overcharge.</li>
                </ul>

                <p><strong>9.2 Non-Refundable Services</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Doctor Consultations & Online Consultations:</strong> No refunds. Rescheduling available.</li>
                  <li><strong>Paramedical Services:</strong> No refunds. Rescheduling available.</li>
                  <li><strong>Medicines:</strong> Non-refundable once delivered. Replacement only for wrong/defective products.</li>
                  <li>Completed consultations or services are non-refundable.</li>
                  <li>No-show appointments without prior rescheduling.</li>
                </ul>

                <p><strong>9.3 Refund Timeline</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Medical Tests & Packages:</strong> Within 72 working hours of cancellation.</li>
                  <li><strong>Pre-dispatch Medicine Cancellations:</strong> Within 5-7 business days.</li>
                  <li>The refund will be credited to the original payment method within <strong>5-10 business days</strong>, depending on your bank or payment provider.</li>
                </ul>

                <p><strong>9.4 Dispute Resolution</strong></p>
                <p>For any disputes, please contact our support team at <strong>support@homecure.com</strong>. We aim to resolve all issues amicably and promptly.</p>
              </div>
            </section>

            {/* Section 10 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">10. LIMITATION OF LIABILITY</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>10.1 Platform Availability</strong></p>
                <p>While we strive to keep the Platform available 24/7, we do not guarantee uninterrupted access. We are not liable for any downtime, technical issues, or service disruptions.</p>

                <p><strong>10.2 Third-Party Services</strong></p>
                <p>HomeCure acts as an intermediary. We are not responsible for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The quality, accuracy, or outcome of services provided by third-party healthcare professionals.</li>
                  <li>Errors or omissions in medical advice, diagnosis, or treatment.</li>
                  <li>Delays or failures caused by service providers.</li>
                </ul>

                <p><strong>10.3 Maximum Liability</strong></p>
                <p>Our liability for any claim arising from your use of the Platform is limited to the amount you paid for the specific service in question.</p>
              </div>
            </section>

            {/* Section 11 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">11. INDEMNIFICATION</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>You agree to indemnify and hold harmless HomeCure, Sanghmitra Healthcare Private Limited, its affiliates, employees, and partners from any claims, damages, losses, or expenses (including legal fees) arising from:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Your violation of these Terms.</li>
                  <li>Your misuse of the Platform.</li>
                  <li>Your violation of any third-party rights.</li>
                </ul>
              </div>
            </section>

            {/* Section 12 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">12. PRIVACY AND DATA PROTECTION</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p>Your use of the Platform is also governed by our <strong>Privacy Policy</strong>, which explains how we collect, use, and protect your personal information. By using the Platform, you consent to our data practices as described in the Privacy Policy.</p>
              </div>
            </section>

            {/* Section 13 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">13. MODIFICATIONS TO TERMS</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>13.1 Right to Modify</strong></p>
                <p>HomeCure reserves the right to update or modify these Terms at any time. Changes will be effective immediately upon posting on the Platform.</p>

                <p><strong>13.2 Notification</strong></p>
                <p>We will notify you of significant changes via email, in-app notification, or a notice on the Platform.</p>

                <p><strong>13.3 Continued Use</strong></p>
                <p>Your continued use of the Platform after changes constitutes acceptance of the updated Terms.</p>
              </div>
            </section>

            {/* Section 14 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">14. TERMINATION</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>14.1 By User</strong></p>
                <p>You may terminate your account at any time by contacting our support team.</p>

                <p><strong>14.2 By HomeCure</strong></p>
                <p>We may suspend or terminate your account immediately if you:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Violate these Terms.</li>
                  <li>Engage in fraudulent or illegal activity.</li>
                  <li>Abuse or harass service providers or other users.</li>
                </ul>

                <p><strong>14.3 Effect of Termination</strong></p>
                <p>Upon termination, your right to use the Platform ceases immediately. We may retain certain information as required by law.</p>
              </div>
            </section>

            {/* Section 15 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-500 rounded-lg flex items-center justify-center">
                  <Scale className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">15. GOVERNING LAW AND JURISDICTION</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>15.1 Governing Law</strong></p>
                <p>These Terms are governed by the laws of India, without regard to conflict of law principles.</p>

                <p><strong>15.2 Jurisdiction</strong></p>
                <p>Any disputes arising from these Terms or your use of the Platform shall be subject to the exclusive jurisdiction of the courts in Patna, Bihar, India.</p>
              </div>
            </section>

            {/* Section 16 */}
            <section>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-green-500 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">16. MISCELLANEOUS</h2>
              </div>
              <div className="pl-13 space-y-3 text-gray-700 leading-relaxed">
                <p><strong>16.1 Entire Agreement</strong></p>
                <p>These Terms, together with our Privacy Policy and Service Agreement, constitute the entire agreement between you and HomeCure.</p>

                <p><strong>16.2 Severability</strong></p>
                <p>If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.</p>

                <p><strong>16.3 Waiver</strong></p>
                <p>Our failure to enforce any right or provision of these Terms does not constitute a waiver of that right or provision.</p>

                <p><strong>16.4 Contact Information</strong></p>
                <p>For questions or concerns regarding these Terms, please contact us at:</p>
                <div className="bg-gray-50 rounded-lg p-4 mt-3">
                  <p><strong>Sanghmitra Healthcare Private Limited</strong></p>
                  <p><strong>Brand:</strong> HomeCure</p>
                  <p>📧 Email: Homecureexpress@gmail.com</p>
                  <p>📧 Support: support@homecure.com</p>
                  <p>📞 Phone: +91 98765 43210</p>
                  <p>📍 Address: Patna, Bihar, India</p>
                  <p>🌐 Website: www.homecureexpress.com</p>
                </div>
              </div>
            </section>

            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-gray-500">
                <strong>Last Updated:</strong> January 1, 2025
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Thank you for choosing HomeCure. We are committed to providing you with safe, reliable, and convenient healthcare services.</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default TermsAndConditions;
